import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Spinner } from 'react-bootstrap';
import editIcon from 'src/assets/icons/icon-edit.svg';
import {
  getSupportConfig,
  patchTenantName,
} from 'src/redux/services/commonServices';
import { setSupportConfig } from 'src/redux/actions/configurationActions';
import { generateSupportConfigs } from 'src/latest/src/core/SupportConfigHandler';
import './PracticeProfile.scss';
import {
  ServicesKeyValueContent2,
  MeetingPreferenceOption,
  PracticeAddress,
  PracticeContactInformation,
} from './index';
import { groupOffice } from 'src/utils/helpers';
import AppButton from 'src/components/base/Button/Button';
import NoData from 'src/latest/src/components/NoData';
import { UserRoles } from 'src/latest/src/constants/UserRoles';
import { Typography } from "src/latest/src/components";

interface SupportMetadata {
  [key: string]: string;
}

const PracticeProfile: React.FC = () => {
  const PracticeProfileMetadata = (choice: {
    metadata: string;
  }): SupportMetadata => {
    const metadataPairs = choice.metadata.split(';');
    const metadata: SupportMetadata = {};

    metadataPairs.forEach(pair => {
      const [key, value] = pair.split(':');

      metadata[key.trim()] = value.trim();
    });

    return metadata;
  };

  const tenantLogo = useSelector(
    (state: any) => state?.configuration?.tenantLogo,
  );
  const tenantName = useSelector(
    (state: any) => state?.configuration?.tenantName,
  );
  const tenantWebsite: string = useSelector(
    (state: any) => state?.configuration?.tenantWebsite,
  );
  const questionnaireService: any = useSelector(
    (state: any) => state?.configuration?.questionnaireService,
  );
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [groupedOffices, setGroupedOffices] = useState<{
    [key: string]: any;
  }>({});
  const loggedInUser = useSelector(
    (state: any) => state?.authentication?.loggedInUser,
  );

  const [editingTenantName, setEditingTenantName] = useState(false);
  const [updateTenantName, setUpdateTenantName] = useState('');
  const [newTenantName, setNewTenantName] = useState('');
  const [editingHover, setEditingHover] = useState(true);

  const isincludesPA = loggedInUser?.user_type?.includes(UserRoles.PRACTICE_ADMIN);

  useEffect(() => {
    setNewTenantName(tenantName);
  }, [tenantName]);

  const handleEditTenantName = () => {
    setEditingTenantName(true);
  };

  const handleUpdateTenantName = async () => {
    try {
      await patchTenantName(updateTenantName);
      setNewTenantName(updateTenantName);
      setEditingTenantName(false);
    } catch (error) {
      console.error('Error updating tenant name:', error);
    }
  };

  const handleCancelEdit = () => {
    setEditingTenantName(false);
    setUpdateTenantName(newTenantName);
  };

  const handleTenantNameChange = (event: any) => {
    setUpdateTenantName(event.target.value);
  };

  const handleMouseEnter = () => {
    setEditingHover(true);
  };

  const fetchSupportConfig = async () => {
    const appConfig: any = await getSupportConfig();
    const supportConfig: any = generateSupportConfigs(appConfig?.config_models);

    dispatch(setSupportConfig(supportConfig));
    setIsLoading(false);
  };

  useEffect(() => {
    fetchSupportConfig();
  }, []);

  useEffect(() => {
    if (questionnaireService && questionnaireService.OFFICE_ADDRESS) {
      const currentChoices: any[] = questionnaireService.OFFICE_ADDRESS.data;

      setGroupedOffices(
        groupOffice(true, currentChoices, PracticeProfileMetadata),
      );
    }
  }, [questionnaireService]);

  return (
    <>
      {isincludesPA ? (
        <div className="page-container-practiceprofile">
          <div className="practicelogo-section bg-white">
            <div
              className="d-logo"
              style={{
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <img
                src={`${decodeURIComponent(tenantLogo)}`}
                alt="practice logo"
                className="practicelogo"
              />
            </div>
            {editingTenantName ? (
              <div className="therapyname-container">
                <input
                  type="text"
                  value={updateTenantName}
                  onChange={handleTenantNameChange}
                  className="input-field"
                />
                <AppButton
                  variant={'primary'}
                  textSize="size-14"
                  textWeight="bold"
                  label="Save"
                  onClick={() => handleUpdateTenantName()}
                />
                <AppButton
                  variant={'secondary'}
                  textSize="size-14"
                  textWeight="bold"
                  label="Cancel"
                  onClick={() => handleCancelEdit()}
                />
              </div>
            ) : (
              <div
                className="therapyname-container"
                onClick={handleEditTenantName}
                onMouseEnter={handleMouseEnter}
              >
                <Typography
                  className="header-profile__therapyname"
                  variant="size-34"
                >
                  {newTenantName}
                </Typography>
                {(editingTenantName || editingHover) && (
                  <img src={editIcon} alt="Edit Icon" className="edit-icon" />
                )}
              </div>
            )}
          </div>

          <div>
            {isLoading ? (
              <div className="th-parcticeprofile-loader">
                <Spinner animation="border" role="status" />
              </div>
            ) : (
              <div className="practice-profile">
                <div className="practice-profile__section">
                  <div className="practice-profile__section__wrapper">
                    <div className="practice-profile__container">
                      <Typography
                        variant="size-14"
                        className="ch-color"
                        weight="bold"
                      >
                        {'SERVICES PROVIDED'}
                      </Typography>
                      <ServicesKeyValueContent2
                        choices={questionnaireService?.PROVIDER_SERVICES?.data}
                        isParentTherapyProfilePage={true}
                        section={undefined}
                        showPage={undefined}
                      />
                    </div>

                    <PracticeContactInformation />

                    <div className="practice-profile__container">
                      <Typography
                        variant="size-14"
                        weight="bold"
                        className="mb-4 ch-color"
                      >
                        {'OFFICE ADDRESSES'}
                      </Typography>
                      <div className="d-flex practice-profile__Address mt-4">
                        {Object.entries(groupedOffices)?.map(
                          ([region, offices]) => {
                            return (
                              <div className="text-center mt-4" key={region}>
                                {region && <h2 className="d-flex">{region}</h2>}
                                <div className="d-flex practice-profile__Address mt-4">
                                  {' '}
                                  <PracticeAddress
                                    choices={offices}
                                    isPracticeAddress={true}
                                  />
                                </div>
                              </div>
                            );
                          },
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="practice-profile__section__wrapper">
                    <div className="practice-profile__container">
                      <Typography
                        variant="size-14"
                        className="mb-4 ch-color"
                        weight="bold"
                      >
                        {'MEETING PREFERENCES'}
                      </Typography>

                      <MeetingPreferenceOption
                        choices={
                          questionnaireService?.MEETING_PREFERENCES?.data
                        }
                        isPracticeProfilePage={true}
                      />
                    </div>
                    <div className="practice-profile__container">
                      <Typography
                        variant="size-14"
                        className="ch-color mb-2"
                        weight="bold"
                      >
                        {'INSURANCE PLAN ACCEPTED'}
                      </Typography>
                      {questionnaireService?.INSURANCES?.data?.map(
                        (insurance: any) => (
                          <div key={insurance.unique_key}>
                            <Typography>{insurance.title}</Typography>
                          </div>
                        ),
                      )}
                    </div>
                    <div className="practice-profile__container">
                      <Typography
                        variant="size-14"
                        className="ch-color"
                        weight="bold"
                      >
                        {'PRACTICE WEBSITE'}
                      </Typography>
                      <style className='tenant-website'>
                        <Typography className="mt-2">
                          <a href={tenantWebsite} target="_blank" rel="noreferrer">{tenantWebsite}</a>
                        </Typography>
                      </style>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div>
          <div className="mt-5">
            <NoData message="You do not have access to view this page" />
          </div>
        </div>
      )}
    </>
  );
};

export default PracticeProfile;
